/**
 * EmptyState Component - Uses shadcn/ui Card and Button
 *
 * Renders a polished, centered empty state with an optional icon medallion,
 * heading, supporting copy, and up to two actions. Backward compatible: a bare
 * `message` (with optional `icon` / `actionLabel` / `onAction`) renders cleanly.
 */
import { memo } from 'react';
import type React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface EmptyStateProps {
  message: string;
  /** Optional bold heading shown above the message. */
  title?: string;
  /** Optional smaller supporting copy shown below the message. */
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
}

const CardAny = Card as React.ElementType;
const CardContentAny = CardContent as React.ElementType;
const ButtonAny = Button as React.ElementType;

const EmptyState = memo(({
  message,
  title,
  description,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  icon: Icon,
  className,
}: EmptyStateProps) => {
  return (
    <CardAny className={cn('p-12 text-center', className)}>
      <CardContentAny className="flex flex-col items-center">
        {Icon && (
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <Icon className="h-6 w-6 text-muted-foreground" />
          </div>
        )}
        {title && (
          <h3 className="mb-1.5 text-sm font-semibold text-foreground">{title}</h3>
        )}
        <p className="max-w-sm text-sm text-muted-foreground">{message}</p>
        {description && (
          <p className="mt-1 max-w-sm text-xs text-muted-foreground/80">{description}</p>
        )}
        {(actionLabel || secondaryActionLabel) && (
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            {actionLabel && onAction && (
              <ButtonAny onClick={onAction}>{actionLabel}</ButtonAny>
            )}
            {secondaryActionLabel && onSecondaryAction && (
              <ButtonAny variant="outline" onClick={onSecondaryAction}>
                {secondaryActionLabel}
              </ButtonAny>
            )}
          </div>
        )}
      </CardContentAny>
    </CardAny>
  );
});

EmptyState.displayName = 'EmptyState';

export default EmptyState;
